import BaseRealizedPresetEffect from './base_realized_preset_effect.js';
import { EVENTS, TIMEOUTS, SELECTORS } from './shared/constants.js';

/**
 * NewRealizedPresetColorLoader
 * 
 * Handles dynamic color dropdown population for designation nodes.
 * When an item model is selected, fetches and populates available colors for that model.
 */
class NewRealizedPresetColorLoader extends BaseRealizedPresetEffect {
  constructor() {
    super();
    this.initialize();
  }

  /**
   * Initialize the color loader functionality
   */
  initialize() {
    this.setupDesignationListeners();
  }

  /**
   * Setup event listeners for designation nodes
   */
  setupDesignationListeners() {
    const nodeContainers = this.getPresetNodeContainers();
    
    nodeContainers.forEach(container => {
      const nodeData = this.getNodeData(container);
      
      if (nodeData.type === 'designation') {
        this.setupDesignationContainer(container, nodeData.index);
      }
    });
  }

  /**
   * Setup a specific designation container
   * @param {HTMLElement} container - The container element
   * @param {string} nodeIndex - The node index
   */
  setupDesignationContainer(container, nodeIndex) {
    const itemModelSelect = container.querySelector('.js-item-model-select');
    const colorSelect = container.querySelector('.js-color-select');
    
    if (!itemModelSelect || !colorSelect) {
      return;
    }

    // Initially hide and disable the color dropdown
    this.hideColorDropdown(colorSelect);

    // Add event listener for item model changes
    itemModelSelect.addEventListener('change', () => {
      this.handleItemModelChange(itemModelSelect, colorSelect, nodeIndex);
    });
  }

  /**
   * Handle item model selection change
   * @param {HTMLElement} itemModelSelect - Item model select element
   * @param {HTMLElement} colorSelect - Color select element
   * @param {string} nodeIndex - Node index
   */
  handleItemModelChange(itemModelSelect, colorSelect, nodeIndex) {
    const itemModelId = itemModelSelect.value;

    if (!itemModelId) {
      this.hideColorDropdown(colorSelect);
      return;
    }

    // Debounce the color loading
    this.debounce(() => {
      this.loadColorsForItemModel(itemModelId, colorSelect, nodeIndex);
    }, TIMEOUTS.COLOR_LOADING_DEBOUNCE, `color_load_${nodeIndex}`);
  }

  /**
   * Load colors for a specific item model
   * @param {string} itemModelId - The item model ID
   * @param {HTMLElement} colorSelect - Color select element
   * @param {string} nodeIndex - Node index
   */
  async loadColorsForItemModel(itemModelId, colorSelect, nodeIndex) {
    const cacheKey = `item_model_colors_${itemModelId}`;

    try {
      // Dispatch loading started event
      this.dispatchNodeEvent(nodeIndex, EVENTS.COLOR_LOADING_STARTED, { 
        itemModelId 
      });

      // Show loading state
      this.showColorLoadingState(colorSelect);

      // Fetch colors for this item model
      const data = await this.fetchData(
        '/realized_presets/fetch_item_model_colors',
        { item_model_id: itemModelId },
        cacheKey,
        nodeIndex
      );

      // Populate the color dropdown
      this.populateColorDropdown(colorSelect, data.colors);
      this.showColorDropdown(colorSelect);

      // Dispatch loading completed event
      this.dispatchNodeEvent(nodeIndex, EVENTS.COLOR_LOADING_COMPLETED, { 
        itemModelId,
        colors: data.colors,
        success: true 
      });

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('NewRealizedPresetColorLoader: Error loading colors:', error);
        this.showColorErrorState(colorSelect);
        
        // Dispatch loading failed event
        this.dispatchNodeEvent(nodeIndex, EVENTS.COLOR_LOADING_FAILED, { 
          itemModelId,
          error: error.message,
          success: false 
        });
      }
    }
  }

  /**
   * Populate color dropdown with options
   * @param {HTMLElement} colorSelect - Color select element
   * @param {Array} colors - Array of [name, id] color options
   */
  populateColorDropdown(colorSelect, colors) {
    // Clear existing options except the prompt
    colorSelect.innerHTML = '<option value="">Choose color...</option>';

    // Add color options
    colors.forEach(([name, id]) => {
      const option = document.createElement('option');
      option.value = id;
      option.textContent = name;
      colorSelect.appendChild(option);
    });

    // Enable the dropdown
    colorSelect.disabled = false;
  }

  /**
   * Show the color dropdown
   * @param {HTMLElement} colorSelect - Color select element
   */
  showColorDropdown(colorSelect) {
    const container = colorSelect.closest('.js-color-dropdown-container');
    if (container) {
      container.style.display = 'block';
      container.style.opacity = '1';
    }
    colorSelect.disabled = false;
  }

  /**
   * Hide the color dropdown
   * @param {HTMLElement} colorSelect - Color select element
   */
  hideColorDropdown(colorSelect) {
    // Clear selection
    colorSelect.value = '';
    
    // Clear options except prompt
    colorSelect.innerHTML = '<option value="">Choose color...</option>';
    
    // Disable the dropdown
    colorSelect.disabled = true;
    
    // Optionally hide the container visually
    const container = colorSelect.closest('.js-color-dropdown-container');
    if (container) {
      container.style.opacity = '0.5';
    }
  }

  /**
   * Show loading state for color dropdown
   * @param {HTMLElement} colorSelect - Color select element
   */
  showColorLoadingState(colorSelect) {
    colorSelect.innerHTML = '<option value="">Loading colors...</option>';
    colorSelect.disabled = true;
    
    const container = colorSelect.closest('.js-color-dropdown-container');
    if (container) {
      container.style.opacity = '0.7';
    }
  }

  /**
   * Show error state for color dropdown
   * @param {HTMLElement} colorSelect - Color select element
   */
  showColorErrorState(colorSelect) {
    colorSelect.innerHTML = '<option value="">Error loading colors</option>';
    colorSelect.disabled = true;
    
    const container = colorSelect.closest('.js-color-dropdown-container');
    if (container) {
      container.style.opacity = '0.5';
    }
  }

  /**
   * Reset all color dropdowns to initial state
   */
  resetAllColorDropdowns() {
    const nodeContainers = this.getPresetNodeContainers();
    
    nodeContainers.forEach(container => {
      const nodeData = this.getNodeData(container);
      
      if (nodeData.type === 'designation') {
        const colorSelect = container.querySelector('.js-color-select');
        if (colorSelect) {
          this.hideColorDropdown(colorSelect);
        }
      }
    });
  }

  /**
   * Get current color selection for a node
   * @param {string} nodeIndex - Node index
   * @returns {string|null} Selected color ID or null
   */
  getColorSelection(nodeIndex) {
    const container = document.querySelector(`[data-node-index="${nodeIndex}"]`);
    if (!container) return null;

    const colorSelect = container.querySelector('.js-color-select');
    return colorSelect ? colorSelect.value : null;
  }

  /**
   * Set color selection for a node
   * @param {string} nodeIndex - Node index
   * @param {string} colorId - Color ID to select
   */
  setColorSelection(nodeIndex, colorId) {
    const container = document.querySelector(`[data-node-index="${nodeIndex}"]`);
    if (!container) return;

    const colorSelect = container.querySelector('.js-color-select');
    if (colorSelect && colorSelect.querySelector(`option[value="${colorId}"]`)) {
      colorSelect.value = colorId;
    }
  }
}

export default NewRealizedPresetColorLoader;
